// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { getRouteParams } from "@selectors";
import { Loader, View, Text, Button, ButtonGroup, SectionComponent } from "@components";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import actionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as selectors from "@modules/employee/selectors";
import { getAddNotificationModal, isLoading } from "@modules/shared/selectors";
import { getPermissions } from "@modules/account/selectors";
import {
  NotificationsIndividuals,
  NotificationsDepartments,
  NotificationsAddModal,
} from "@modules/shared/components";
import type { EmployeeType } from "@modules/employee/types";
import { EMPLOYEE_REF_TYPES } from "@modules/shared/constants/notifications";
import type { AddNotificationModal } from "@modules/shared/reducer/employee-notifications/types";

export const PAGE_CONTENT_TYPE = {
  NOTIFICATIONS_FOR: "notificationsFor",
  NOTIFICATIONS_TO: "notificationsTo",
};

type PropsFromState = $Exact<{
  isLoading?: boolean,
  employeeId?: number,
  employee: EmployeeType,
  employeeName: string,
  permissions: { [permission: string]: boolean } | null,
  modal: AddNotificationModal,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof actionCreators.GetEmployees,
  toggleAddNotificationModal: typeof sharedActionCreators.ToggleAddNotificationModal,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromClientContext,
}>;

type OwnState = $Exact<{
  contentType: string,
}>;

export class EmployeeContacts extends React.Component<Props, OwnState> {
  state = {
    contentType: PAGE_CONTENT_TYPE.NOTIFICATIONS_TO,
  };

  componentDidMount() {
    this.props.getEmployees({
      clientId: this.props.clientId,
      filters: {
        id: this.props.employeeId,
      },
    });
  }

  showAddNotificationModal = () => {
    this.props.toggleAddNotificationModal(true);
  };

  changeContentType = () => {
    this.setState({
      contentType:
        this.state.contentType === PAGE_CONTENT_TYPE.NOTIFICATIONS_TO
          ? PAGE_CONTENT_TYPE.NOTIFICATIONS_FOR
          : PAGE_CONTENT_TYPE.NOTIFICATIONS_TO,
    });
    this.props.getEmployees({
      clientId: this.props.clientId,
      filters: {
        id: this.props.employeeId,
      },
    });
  };

  getButtonState = (buttonType: string) => {
    return this.state.contentType === buttonType ? "selected" : null;
  };

  get employeeRefType() {
    return this.state.contentType === PAGE_CONTENT_TYPE.NOTIFICATIONS_TO
      ? EMPLOYEE_REF_TYPES.RECIPIENT
      : EMPLOYEE_REF_TYPES.TARGET;
  }

  get hasAdminPermissions() {
    // Only users with admin permissions should be able to create/edit data
    let res = !!(
      this.props.permissions &&
      this.props.permissions[PermissionEnum.PRIVILEGE_EMPLOYEE_NOTIFICATION_CONTACTS_OPERATIONS]
    );

    return res;
  }

  get isNotificationsTo() {
    return this.state.contentType === PAGE_CONTENT_TYPE.NOTIFICATIONS_TO;
  }

  get renderInfoText() {
    return (
      <View direction="row" alignItems="center" gap="1">
        <span className="material-icons ">person</span>
        <span className="material-icons ">
          {this.isNotificationsTo ? "arrow_forward" : "arrow_back"}
        </span>
        <span
          dangerouslySetInnerHTML={{
            __html: this.props.t(
              this.isNotificationsTo
                ? "NOTIFICATION_CONTACT_INFO_TEXT_RECIPIENT"
                : "NOTIFICATION_CONTACT_INFO_TEXT_TARGET",
              {
                ownerName: this.props.employeeName,
              },
            ),
          }}
        />
      </View>
    );
  }

  render() {
    const { contentType } = this.state;
    return (
      <View position="relative" gap={6}>
        {this.props.isLoading && <Loader />}
        <Text className="message-box" text={this.props.t("EMPLOYEE_CONTACTS_TAB_INFO")} />
        <View gap="4" className="gs-container">
          <View direction="row">
            <View direction="row" alignItems="center" gap="4" flex="1">
              <Text type="h5" text={this.props.t("NOTIFICATIONS")} />

              <ButtonGroup>
                <Button
                  key={PAGE_CONTENT_TYPE.NOTIFICATIONS_TO}
                  color={this.getButtonState(PAGE_CONTENT_TYPE.NOTIFICATIONS_TO)}
                  onClick={this.changeContentType}
                >
                  {this.props.t("SENT_TO")}
                </Button>
                <Button
                  key={PAGE_CONTENT_TYPE.NOTIFICATIONS_FOR}
                  color={this.getButtonState(PAGE_CONTENT_TYPE.NOTIFICATIONS_FOR)}
                  onClick={this.changeContentType}
                >
                  {this.props.t("RECEIVED_FOR")}
                </Button>
              </ButtonGroup>
              {this.renderInfoText}
            </View>
            {this.hasAdminPermissions && (
              <Button
                materialIcon="add_box"
                className="primary"
                onClick={this.showAddNotificationModal}
              >
                {this.props.t(
                  this.isNotificationsTo ? "NOTIFICATIONS_ADD_RECIPIENT" : "ADD_EMPLOYEE",
                )}
              </Button>
            )}
          </View>

          <NotificationsIndividuals
            clientId={this.props.clientId}
            employeeId={this.props.employeeId}
            employeeName={this.props.employeeName}
            employeeRefType={this.employeeRefType}
            allowEdit={this.hasAdminPermissions}
            contentType={contentType}
            deleteModalTitle={this.props.t("DELETE_NOTIFICATION")}
            deleteModalContent={this.props.t("DELETE_NOTIFICATION_CONFIRMATION")}
            deleteModalContentPlural={this.props.t("DELETE_NOTIFICATIONS_CONFIRMATION")}
          />
        </View>
        <SectionComponent
          title={
            <h5>
              {this.props.t(
                this.isNotificationsTo
                  ? "NOTIFICATIONS_DEPARTMENT_RECIPIENTS"
                  : "NOTIFICATIONS_DEPARTMENT",
              )}
            </h5>
          }
          className="gs-container"
        >
          <NotificationsDepartments
            clientId={this.props.clientId}
            employeeId={this.props.employeeId}
            employeeRefType={this.employeeRefType}
          />
        </SectionComponent>
        {this.props.modal.visible && (
          <NotificationsAddModal
            clientId={this.props.clientId}
            employeeId={this.props.employeeId}
            employeeRefType={this.employeeRefType}
            employee={this.props.employee}
            isLoading={this.props.isLoading}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);
  const employee = selectors.getEmployee(employeeId)(state);
  const employeeName = selectors.getEmployeeName(employee);

  return {
    isLoading: selectors.isLoading(state) || isLoading(state),
    employeeId,
    employee,
    employeeName,
    permissions: getPermissions(state),
    modal: getAddNotificationModal(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: actionCreators.GetEmployees,
  toggleAddNotificationModal: sharedActionCreators.ToggleAddNotificationModal,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withClientContext(),
  withPermissions({
    permissionId: PermissionEnum.ACCESS_REPORTING_EMPLOYEES,
  }),
)(EmployeeContacts);
